<template>
  <button
    type="button"
    class="chat-ref chat-ref-system"
    :class="{ 'is-unknown': !system }"
    v-tooltip="tooltip"
    @click.stop="onClick($event)">
    <span class="chat-ref-icon">◈</span>
    <span class="chat-ref-label">{{ displayLabel }}</span>
  </button>
</template>

<script>
import { navigateRef } from '../refNavigation';

/**
 * Renders a `[[sys:123|Sol]]` chip in a chat message body.
 * Click flies the galaxy camera to the system. If the user is currently
 * inside a system view, that view is closed first so the camera move is
 * actually visible.
 *
 * If the referenced system isn't in the client's visible data (e.g. zero
 * contact), the chip degrades to a non-interactive label — the click
 * still fires but resolves to a no-op via the unknown branch.
 */
export default {
  name: 'chat-ref-system',
  // Game.vue provides the MapData singleton. The default is `null` so
  // tests/storybook can mount the component in isolation without
  // wiring up an ancestor — the chip just falls back to "Unknown" in
  // that case.
  inject: {
    mapData: { default: null },
  },
  props: {
    id: { type: String, required: true },
    label: { type: String, default: null },
  },
  computed: {
    systemId() {
      const n = parseInt(this.id, 10);
      return Number.isFinite(n) ? n : null;
    },
    system() {
      if (this.systemId == null) return null;
      // mapData is not reactive (Game.vue keeps it out of data() on
      // purpose), so depend on the store's replace-only galaxy root:
      // chips rendered before the join reply landed re-resolve when the
      // galaxy (re)loads. The value itself is unused — the read is the dep.
      const galaxyGeneration = this.$store.state.game.galaxy;
      if (!galaxyGeneration || !this.mapData || !this.mapData.systems) return null;
      return this.mapData.systems.find((s) => s.id === this.systemId) || null;
    },
    displayLabel() {
      if (this.label) return this.label;
      if (this.system && this.system.name) return this.system.name;
      return `Sys ${this.id}`;
    },
    tooltip() {
      if (!this.system) {
        return this.$t ? this.$t('in_game_chat.ref.unknown_system') : 'Unknown system';
      }
      const { x, y } = this.system.position || {};
      const coords = (x != null && y != null)
        ? ` (${x.toFixed(1)}, ${y.toFixed(1)})`
        : '';
      return `${this.system.name || this.displayLabel}${coords}`;
    },
  },
  methods: {
    // Shift+click copies the chip into the composer (to answer about the
    // same system); a plain click flies there.
    onClick(event) {
      if (!this.system) return;

      if (event && event.shiftKey) {
        this.$root.$emit('chat:insertRef', { kind: 'sys', id: this.id, label: this.displayLabel });
        return;
      }

      navigateRef(this, 'sys', this.id);
    },
  },
};
</script>
